// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Button,
    Icon,
    List,
    Translate,
} from '@plesk/ui-library';
import SettingsWrapper from 'admin/settings/containers/Settings/SettingsWrapper';
import * as settingsActions from 'common/modules/settings/actions';
import { connect } from 'react-redux';
import {
    Dispatch,
    bindActionCreators,
} from 'redux';
import {
    NotificationEvent,
    NOTIFICATION_RECIPIENT_GROUP,
} from 'common/api/resources/Settings';
import { RootState } from 'admin/core/store';
import { StyledActions } from 'common/components/Actions/Styles';
import {
    ICONS,
    SIZE,
} from 'common/constants';
import { StyledTable } from 'common/components/styles/StyledTable';
import { Dialog } from 'common/components/Dialog/Dialog';
import NotificationSettingsEditDialog from 'admin/settings/containers/Settings/NotificationSettingsEditDialog';
import { getActionColumnProps } from 'common/helpers/list';
import { dataCySelector } from 'common/tests/selectors';
import { NOTIFICATION_SETTINGS } from 'admin/settings/constants/tests';
import { FormFieldSwitch } from 'common/components/Form/FormFieldSwitch/FormFieldSwitch';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import { FEATURES } from 'common/modules/app/features/constants';
import { isFeatureFlagEnabled } from 'common/modules/app/features/utils';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import {
    Tab,
    TabList,
    TabPanel,
    Tabs,
} from 'react-tabs';
import { NOTIFICATIONS_TABS } from 'admin/settings/constants/constants';

export const NotificationTranslationMap = {
    [NotificationEvent.SERVER_CREATE]: 'settings.notifications.serverCreate',
    [NotificationEvent.SERVER_RESET_PASSWORD]: 'settings.notifications.serverResetPassword',
    [NotificationEvent.USER_RESET_PASSWORD]: 'settings.notifications.userResetPassword',
    [NotificationEvent.USER_VERIFY_EMAIL]: 'settings.notifications.userVerifyEmail',
    [NotificationEvent.TWO_FACTOR_AUTH_RECOVERY_CODE]: 'settings.notifications.twoFactorAuthRecoveryCode',
    [NotificationEvent.PROJECT_USER_INVITE]: 'settings.notifications.projectUserInvite',
    [NotificationEvent.PROJECT_USER_LEFT]: 'settings.notifications.projectUserLeft',
    [NotificationEvent.SERVER_INCOMING_TRAFFIC_EXCEEDED]: 'settings.notifications.serverIncomingTrafficExceeded',
    [NotificationEvent.SERVER_OUTGOING_TRAFFIC_EXCEEDED]: 'settings.notifications.serverOutgoingTrafficExceeded',
};

export type NotificationsSettingsProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type NotificationsSettingsInternalProps =
    RouteComponentProps &
    NotificationsSettingsProps;

const columns = [
    {
        key: 'colEvent',
        title: <Translate content="settings.notifications.table.event" />,
    }, {
        key: 'colSend',
        title: <Translate content="settings.notifications.table.send" />,
        align: 'center' as const,
    },
    getActionColumnProps(),
];

export const NotificationsSettings: React.FC<NotificationsSettingsProps> = (props) => {
    const {
        notifications,
        isSavingSettings,
        // todo: SVM2-6974 delete this when high availability for CR is ready
        isHighAvailabilityCrFeatureFlagEnabled,
        settingsActions: { saveSettings },
    } = props;
    const { history, location } = props as NotificationsSettingsInternalProps;
    const hash = location?.hash ?? '';

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.SETTINGS.NOTIFICATIONS.VISIT);
    }, []);

    // todo: SVM2-6974 delete this when high availability for CR is ready
    const tabs = Object.values(NOTIFICATIONS_TABS);
    const selectedTabIndex = Math.max(0, tabs.indexOf(hash as NOTIFICATIONS_TABS));
    const handleTabSelect = (index: number) => history.replace(tabs[index]);

    const [isOpen, setIsOpen] = React.useState(false);
    const [savingEvent, setSavingEvent] = React.useState(NotificationEvent.SERVER_CREATE);
    const [editEvent, setEditEvent] = React.useState<string>(NotificationEvent.SERVER_CREATE);

    const handleOpenEditDialog = (event: string) => () => {
        setIsOpen(true);
        setEditEvent(event);
    };
    const handleCloseEditDialog = () => setIsOpen(false);

    const handleSwitchEvent = React.useCallback((event: string) => () => {
        setSavingEvent(event as NotificationEvent);
        saveSettings({
            notifications: {
                ...notifications,
                [event]: {
                    ...notifications[event],
                    enabled: !notifications[event].enabled,
                },
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications]);

    const buildRows = React.useCallback((filterRecipientGroup: NOTIFICATION_RECIPIENT_GROUP) => {
        const rows = [];

        for (const event in notifications) {
            if (Object.prototype.hasOwnProperty.call(notifications, event)) {
                const settings = notifications[event];

                if (settings.recipient_group !== filterRecipientGroup) {
                    continue;
                }

                rows.push({
                    colEvent: <Translate content={NotificationTranslationMap[event]} />,
                    colSend: (
                        <FormFieldSwitch
                            loading={isSavingSettings && savingEvent === event}
                            disabled={isSavingSettings}
                            onChange={handleSwitchEvent(event)}
                            name={`notifications[${event}][enabled]`}
                            label={<></>}
                            data-cy={dataCySelector(event, NOTIFICATION_SETTINGS.ENABLED)}
                        />
                    ),
                    colActions: (
                        <StyledActions>
                            <Button
                                ghost={true}
                                className="action-icon"
                                onClick={handleOpenEditDialog(event)}
                                icon={<Icon name={ICONS.PENCIL_DIALOG} />}
                                data-cy={dataCySelector(event, NOTIFICATION_SETTINGS.ACTION_EDIT)}
                            />
                        </StyledActions>
                    ),
                    key: event,
                });
            }
        }

        return rows;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifications, handleSwitchEvent, isSavingSettings, savingEvent]);

    const adminData = React.useMemo(
        () => buildRows(NOTIFICATION_RECIPIENT_GROUP.ADMIN),
        [buildRows]
    );

    const clientData = React.useMemo(
        () => buildRows(NOTIFICATION_RECIPIENT_GROUP.OWNER),
        [buildRows]
    );

    return (
        <>
            <SettingsWrapper title="settings.titles.notifications">
                {/* todo: SVM2-6974 delete this when high availability for CR is ready */}
                {isHighAvailabilityCrFeatureFlagEnabled ? (
                    <Tabs selectedIndex={selectedTabIndex} onSelect={handleTabSelect}>
                        <TabList>
                            <Tab>
                                <Translate content="settings.notifications.tabs.admin" />
                            </Tab>
                            <Tab>
                                <Translate content="settings.notifications.tabs.client" />
                            </Tab>
                        </TabList>
                        <TabPanel>
                            {adminData.length > 0 && (
                                <StyledTable>
                                    <List
                                        columns={columns}
                                        data={adminData}
                                    />
                                </StyledTable>
                            )}
                        </TabPanel>
                        <TabPanel>
                            {clientData.length > 0 && (
                                <StyledTable>
                                    <List
                                        columns={columns}
                                        data={clientData}
                                    />
                                </StyledTable>
                            )}
                        </TabPanel>
                    </Tabs>
                ) : (
                    <StyledTable>
                        <List
                            columns={columns}
                            data={clientData}
                        />
                    </StyledTable>
                )}
            </SettingsWrapper>
            <Dialog
                heading={(
                    <Translate content={'settings.notifications.editNotification'} />
                )}
                closeHandler={handleCloseEditDialog}
                isOpen={isOpen}
                size={SIZE.MD}
                data-cy={NOTIFICATION_SETTINGS.EDIT_DIALOG}
            >
                <NotificationSettingsEditDialog
                    event={editEvent}
                />
            </Dialog>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    notifications: state.settings.notifications,
    isSavingSettings: state.app.loadingFlags.has(LOADING_FLAGS.SAVE_APP_SETTINGS),
    // todo: SVM2-6974 delete this when high availability for CR is ready
    isHighAvailabilityCrFeatureFlagEnabled: isFeatureFlagEnabled(state, FEATURES.HIGH_AVAILABILITY_CR),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    settingsActions: bindActionCreators(settingsActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NotificationsSettings));
